/**
 * AppContent Component
 *
 * Handles sequential data preloading on app startup with splash screen.
 * Wraps the main application routes and manages the splash screen visibility.
 *
 * US-015: Updated routing structure with new sidebar navigation paths.
 */

import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useDataCache } from '@/contexts/DataCacheContext'
import SplashScreen from './SplashScreen'

// Dashboard / Home
import CommandCentre from './dashboard/CommandCentre'

// Page Components
import SalesRecord from './SalesRecord'
import CreateSalePage from './CreateSalePage'
import CustomerDashboard from './CustomerDashboard'
import ProductDashboard from './ProductDashboard'
import PaymentDashboard from './PaymentDashboard'
import InvoiceDashboard from './InvoiceDashboard'
import ReportsDashboard from './ReportsDashboard'
import FarmSettings from './FarmSettings'
import LocationSettings from './settings/LocationSettings'
import RegistrationRequestsList from './registration/RegistrationRequestsList'
import PlaceholderPage from './PlaceholderPage'

// Import & Dev Tools
import ImportWizardV2 from './ImportWizardV2'
import ImportHistory from './ImportHistory'
import GoogleSheetsImport from './GoogleSheetsImport'
import TestProductConfig from './TestProductConfig'
import ReviewViewPrototypePage from './import-wizard/ReviewViewPrototypePage'
import XeroAuthCallback from './xero/XeroAuthCallback'
import ErrorBoundary from './ErrorBoundary'

// Delivery
import DeliveryRunView from './delivery/DeliveryRunView'

// Production
import ProductionTimeline from './production/ProductionTimeline'

// Sales Timeline
import SalesTimelinePage from './sales/timeline/SalesTimelinePage'
import QuickOrderPage from '@/pages/sales/QuickOrderPage'
import PredictionModelScreen from './sales/PredictionModelScreen'
import { StrainsListPage, StrainDetailPage } from './production/strains'
import { ProcessListPage, ProcessEditorKanban } from './production/process'
import { LCList } from './production/liquid-culture'
import { SpawnBatchList } from './production/spawn'
import { SubstrateBatchList } from './production/substrate'
import { InoculationEventsList } from './production/inoculation'
import HeatCyclingPage from '@/pages/production/HeatCyclingPage'
import ShooglingPage from '@/pages/production/ShooglingPage'
import IncubationPage from '@/pages/production/IncubationPage'
import TimetablePage from '@/pages/production/TimetablePage'
import SubstrateLogsPage from '@/pages/production/SubstrateLogsPage'

// Tasks (Top-level)
import { TasksPageCalendar } from './tasks'

// Alerts (Planner)
import { AlertsPage } from '@/pages/alerts'
import HarvestDashboard from './production/HarvestDashboard'
import { ProductionForecastDashboard } from './production/forecasting'
import InspectionsPage from '@/pages/production/InspectionsPage'
import { DemandPlanningTimeline } from './demand-planning'

// Inventory
import InventoryPage from '@/pages/inventory/InventoryPage'
import SuppliersPage from '@/pages/inventory/SuppliersPage'
import InventoryItemsPage from '@/pages/inventory/InventoryItemsPage'
import BatchesPage from '@/pages/inventory/BatchesPage'

// Planner
import PlannerPage from '@/pages/planner/PlannerPage'

// Markets
import MarketsPage from '@/pages/markets/MarketsPage'
import MarketEventDetail from '@/pages/markets/MarketEventDetail'
import SquareAuthCallback from '@/components/markets/SquareAuthCallback'

// Costs
import FixedCostsPage from '@/pages/costs/FixedCostsPage'
import CostSummaryPage from '@/pages/costs/CostSummaryPage'
import EnergyPage from '@/pages/costs/EnergyPage'

// Analytics
import { ProfitabilityDashboardPage, BreakevenDashboardPage } from '@/pages/analytics'

// Module-level flag to track if preload has completed this session
// This survives React Strict Mode remounts unlike useRef
let hasCompletedPreload = false

export default function AppContent({ user, onLogout, sharedReportsData, reportsDataLoading, preloadReportsData }) {
  const { getCustomers, getProducts, getProductTypes, getFarmInfo,
          getPriceTiers, getWholesaleThresholds, getVolumeDiscounts, getProductPrices,
          getActivePromotions, snapshot } = useDataCache()

  // Start with preloading=false if we've already completed preload this session
  const [isPreloading, setIsPreloading] = useState(!hasCompletedPreload)
  const [preloadProgress, setPreloadProgress] = useState(hasCompletedPreload ? 100 : 0)
  const [preloadStage, setPreloadStage] = useState(hasCompletedPreload ? 'complete' : 'farm')

  useEffect(() => {
    // Skip preload if: no user, already completed this session, or cache already has data
    const cacheHasData = (snapshot.customers?.length > 0) || (snapshot.products?.length > 0)
    if (!user || hasCompletedPreload || cacheHasData) {
      if (cacheHasData && !hasCompletedPreload) {
        console.log('[Preload] Skipping - cache already has data')
        hasCompletedPreload = true
      }
      setIsPreloading(false)
      return
    }

    const preloadData = async () => {
      try {
        setIsPreloading(true)

        // Sequential loading stages with progress updates
        // All 8 datasets preloaded so the sales modal opens instantly
        const stages = [
          { name: 'farm', label: 'Loading farm information', fn: () => getFarmInfo(), progress: 10 },
          { name: 'products', label: 'Loading products', fn: () => getProducts(), progress: 25 },
          { name: 'customers', label: 'Loading customers', fn: () => getCustomers(), progress: 40 },
          { name: 'productTypes', label: 'Loading product types', fn: () => getProductTypes(), progress: 50 },
          { name: 'priceTiers', label: 'Loading price tiers', fn: () => getPriceTiers(), progress: 60 },
          { name: 'wholesaleThresholds', label: 'Loading wholesale thresholds', fn: () => getWholesaleThresholds(), progress: 70 },
          { name: 'volumeDiscounts', label: 'Loading volume discounts', fn: () => getVolumeDiscounts(), progress: 75 },
          { name: 'productPrices', label: 'Loading product prices', fn: () => getProductPrices(), progress: 85 },
          { name: 'activePromotions', label: 'Loading promotions', fn: () => getActivePromotions(), progress: 90 }
        ]

        for (const stage of stages) {
          setPreloadStage(stage.name)
          console.log(`[Preload] Starting: ${stage.label}`)

          try {
            await stage.fn()
            setPreloadProgress(stage.progress)
          } catch (error) {
            console.error(`[Preload] Error in ${stage.name}:`, error)
            // Don't fail completely - continue loading other data
          }
        }

        // All data loaded successfully
        setPreloadStage('complete')
        setPreloadProgress(100)
        hasCompletedPreload = true
        console.log('[Preload] All 8 datasets loaded successfully')

        // Minimum splash screen display time for UX
        await new Promise(resolve => setTimeout(resolve, 1500))

      } catch (error) {
        console.error('[Preload] Unexpected error:', error)
      } finally {
        setIsPreloading(false)
      }
    }

    preloadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, snapshot.customers?.length, snapshot.products?.length]) // Check cache state to skip preload if data exists

  // Show splash screen during preload
  if (isPreloading) {
    return (
      <SplashScreen
        isLoading={true}
        progress={preloadProgress}
        stage={preloadStage}
      />
    )
  }

  return (
    <Routes>
      {/* ============================================
          NEW ROUTES (US-015 Sidebar Navigation)
          ============================================ */}

      {/* Home / Command Centre */}
      <Route path="/" element={<CommandCentre user={user} onLogout={onLogout} />} />

      {/* Tasks (Top-level) */}
      <Route path="/tasks" element={<TasksPageCalendar user={user} onLogout={onLogout} />} />

      {/* Alerts (Planner) */}
      <Route path="/alerts" element={<AlertsPage user={user} onLogout={onLogout} />} />

      {/* Sales Section */}
      <Route path="/sales/quick" element={<QuickOrderPage user={user} onLogout={onLogout} />} />
      <Route path="/sales/orders" element={<SalesRecord user={user} onLogout={onLogout} />} />
      <Route path="/sales/new" element={<CreateSalePage user={user} onLogout={onLogout} />} />
      <Route path="/sales/invoicing" element={<InvoiceDashboard user={user} onLogout={onLogout} />} />
      <Route path="/sales/pricing" element={<ProductDashboard user={user} onLogout={onLogout} defaultTab="pricing" />} />
      <Route path="/sales/timeline" element={<SalesTimelinePage user={user} onLogout={onLogout} />} />
<Route path="/sales/predictions" element={<PredictionModelScreen user={user} onLogout={onLogout} />} />
      <Route path="/sales/markets" element={<MarketsPage user={user} onLogout={onLogout} />} />
      <Route path="/sales/markets/:eventId" element={<MarketEventDetail user={user} onLogout={onLogout} />} />

      {/* Customers */}
      <Route path="/customers" element={<CustomerDashboard user={user} onLogout={onLogout} />} />

      {/* Production Section */}
      <Route path="/production" element={<ProductionTimeline user={user} onLogout={onLogout} />} />
      <Route path="/production/timeline" element={<ProductionTimeline user={user} onLogout={onLogout} />} />
      <Route path="/production/strains" element={<StrainsListPage user={user} onLogout={onLogout} />} />
      <Route path="/production/strains/:id" element={<StrainDetailPage user={user} onLogout={onLogout} />} />
      <Route path="/production/process" element={<ProcessListPage user={user} onLogout={onLogout} />} />
      <Route path="/production/process/:id" element={<ProcessEditorKanban user={user} onLogout={onLogout} />} />
      <Route path="/production/lc" element={<LCList user={user} onLogout={onLogout} />} />
      <Route path="/production/spawn" element={<SpawnBatchList user={user} onLogout={onLogout} />} />
      <Route path="/production/substrate" element={<SubstrateBatchList user={user} onLogout={onLogout} />} />
      <Route path="/production/inoculations" element={<InoculationEventsList user={user} onLogout={onLogout} />} />
      <Route path="/production/harvests" element={<HarvestDashboard user={user} onLogout={onLogout} />} />
      <Route path="/production/timetable" element={<TimetablePage user={user} onLogout={onLogout} />} />
      <Route path="/production/tasks" element={<Navigate to="/tasks" replace />} />
      <Route path="/production/inspections" element={<InspectionsPage user={user} onLogout={onLogout} />} />
      <Route path="/production/forecasting" element={<ProductionForecastDashboard user={user} onLogout={onLogout} />} />
      <Route path="/production/demand" element={<Navigate to="/diagnostics/demand" replace />} />
      <Route path="/diagnostics/demand" element={<DemandPlanningTimeline user={user} onLogout={onLogout} />} />
      <Route path="/production/heat-cycling" element={<HeatCyclingPage user={user} onLogout={onLogout} />} />
      <Route path="/production/shoogling" element={<ShooglingPage user={user} onLogout={onLogout} />} />
      <Route path="/production/incubation" element={<IncubationPage user={user} onLogout={onLogout} />} />
      <Route path="/production/substrate-logs" element={<SubstrateLogsPage user={user} onLogout={onLogout} />} />

      {/* Planner */}
      <Route path="/planner" element={<PlannerPage user={user} onLogout={onLogout} />} />

      {/* Deliveries (Mobile-only) */}
      <Route path="/deliveries" element={<DeliveryRunView user={user} onLogout={onLogout} />} />

      {/* Reporting */}
      <Route
        path="/reporting"
        element={
          <ReportsDashboard
            user={user}
            onLogout={onLogout}
            sharedReportsData={sharedReportsData}
            reportsDataLoading={reportsDataLoading}
            preloadReportsData={preloadReportsData}
          />
        }
      />

      {/* Inventory Section */}
      <Route path="/inventory" element={<InventoryPage user={user} onLogout={onLogout} />} />
      <Route path="/inventory/suppliers" element={<SuppliersPage user={user} onLogout={onLogout} />} />
      <Route path="/inventory/items" element={<InventoryItemsPage user={user} onLogout={onLogout} />} />
      <Route path="/inventory/purchases" element={<BatchesPage user={user} onLogout={onLogout} />} />

      {/* Costs Section */}
      <Route path="/costs" element={<Navigate to="/costs/summary" replace />} />
      <Route path="/costs/fixed" element={<FixedCostsPage user={user} onLogout={onLogout} />} />
      <Route path="/costs/summary" element={<CostSummaryPage user={user} onLogout={onLogout} />} />
      <Route path="/costs/energy" element={<EnergyPage user={user} onLogout={onLogout} />} />

      {/* Analytics Section */}
      <Route path="/analytics" element={<Navigate to="/analytics/profitability" replace />} />
      <Route path="/analytics/profitability" element={<ProfitabilityDashboardPage user={user} onLogout={onLogout} />} />
      <Route path="/analytics/breakeven" element={<BreakevenDashboardPage user={user} onLogout={onLogout} />} />

      {/* Settings Section */}
      <Route path="/settings/products" element={<ProductDashboard user={user} onLogout={onLogout} />} />
      <Route path="/settings/locations" element={<LocationSettings user={user} onLogout={onLogout} />} />
      <Route path="/settings/system" element={<FarmSettings user={user} onLogout={onLogout} />} />
      <Route path="/settings/registration-requests" element={<RegistrationRequestsList user={user} onLogout={onLogout} />} />

      {/* ============================================
          LEGACY ROUTE REDIRECTS
          Keep old routes working during transition
          ============================================ */}

      {/* Old dashboard -> new home */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />

      {/* Old sales -> new sales/orders */}
      <Route path="/sales" element={<Navigate to="/sales/orders" replace />} />

      {/* Old invoices -> new sales/invoicing */}
      <Route path="/invoices" element={<Navigate to="/sales/invoicing" replace />} />

      {/* Old products -> new settings/products */}
      <Route path="/products" element={<Navigate to="/settings/products" replace />} />

      {/* Old payments -> sales/invoicing (payments are on invoicing now) */}
      <Route path="/payments" element={<Navigate to="/sales/invoicing" replace />} />

      {/* Old reports -> new reporting */}
      <Route path="/reports" element={<Navigate to="/reporting" replace />} />

      {/* Old farm-settings & settings -> new settings/system */}
      <Route path="/farm-settings" element={<Navigate to="/settings/system" replace />} />
      <Route path="/settings" element={<Navigate to="/settings/system" replace />} />

      {/* ============================================
          UTILITY & DEV ROUTES
          ============================================ */}

      {/* Import Wizard */}
      <Route path="/import-wizard" element={<ErrorBoundary><ImportWizardV2 user={user} onLogout={onLogout} /></ErrorBoundary>} />
      <Route path="/import-wizard-v2" element={<ErrorBoundary><ImportWizardV2 user={user} onLogout={onLogout} /></ErrorBoundary>} />
      <Route path="/import" element={<ErrorBoundary><ImportWizardV2 user={user} onLogout={onLogout} /></ErrorBoundary>} />
      <Route path="/import-history" element={<ImportHistory user={user} onLogout={onLogout} />} />
      <Route path="/google-sheets-import" element={<GoogleSheetsImport user={user} onLogout={onLogout} />} />

      {/* Dev Tools */}
      <Route path="/test-product-config" element={<TestProductConfig user={user} onLogout={onLogout} />} />
      <Route path="/review-prototype" element={<ReviewViewPrototypePage user={user} onLogout={onLogout} />} />


      {/* OAuth Callbacks */}
      <Route path="/xero/callback" element={<XeroAuthCallback user={user} />} />
      <Route path="/settings/square/callback" element={<SquareAuthCallback user={user} />} />

      {/* Catch-all -> Home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
